/**
 * Helpers for resolving vertex-group → face assignments.
 *
 * A face "belongs to" a vertex group iff every one of its corners is in the
 * group's index set (matches the all-vertices-in semantics that
 * `resolveFaceMaterialIndices` uses for material slot binding). Where multiple
 * groups claim the same face the later group in `obj.vertexGroups` wins —
 * same overlay precedence as material slot binding.
 */

import type { PolygonTopology, VertexGroup } from "./types.ts";

/**
 * Per-face vertex-group index. `result[faceIndex]` is the group index into
 * `vertexGroups`, or `-1` if no group fully contains the face.
 *
 * Returns `null` when there are no non-empty groups (caller can short-circuit
 * any rendering / iteration).
 */
export function resolveFaceVertexGroupIndices(
  topology: PolygonTopology,
  vertexGroups: ReadonlyArray<VertexGroup> | undefined,
): Int32Array | null {
  const groups = vertexGroups;
  if (!groups || groups.length === 0) return null;

  const { offsets, corners } = topology;
  const faceCount = offsets.length - 1;
  if (faceCount === 0) return null;

  // Skip empty groups up front — they can never claim a face, and skipping
  // them keeps the inner loop tight when users have many empty placeholders.
  const bindings: { groupIdx: number; members: Set<number> }[] = [];
  for (let i = 0; i < groups.length; i++) {
    const members = groups[i].indices;
    if (members.size > 0) bindings.push({ groupIdx: i, members });
  }
  if (bindings.length === 0) return null;

  const out = new Int32Array(faceCount);
  out.fill(-1);
  for (let f = 0; f < faceCount; f++) {
    const start = offsets[f];
    const end = offsets[f + 1];
    if (end <= start) continue;
    let resolved = -1;
    for (const b of bindings) {
      let allIn = true;
      for (let c = start; c < end; c++) {
        if (!b.members.has(corners[c])) {
          allIn = false;
          break;
        }
      }
      if (allIn) resolved = b.groupIdx;
    }
    out[f] = resolved;
  }
  return out;
}
